/**
 * Maps protected with a password.
 *
 * A protected map is stored as a *shell*: the map's row (so the library can list it by name) and a
 * single root topic whose metadata carries the envelope — the map's topics, links and appearance,
 * encrypted with a key derived from the password. Nothing else of the content is stored, synced or
 * journalled in the clear. Opening it asks for the password, decrypts the envelope into a document
 * that lives only in memory, and every save writes a fresh envelope back to the shell's root.
 *
 * The cryptography is standard and interoperable: PBKDF2-HMAC-SHA256 for the key, AES-256-GCM for
 * the content (a 12-byte nonce, a 16-byte tag appended to the ciphertext — WebCrypto's own layout).
 * The phone and the browser implement `MapCipher` with their own primitives and read each other's
 * envelopes. The iteration count travels in the envelope, so it can rise later without breaking
 * maps protected today.
 *
 * What it does not do, said plainly so the interface can say it too: there is no recovery. A
 * forgotten password is a lost map — no copy of the key exists anywhere, which is the point.
 */
import { fromBase64Url, toBase64Url, } from '@orbit-mind/shared-types';
import { richTextFromPlain } from './richText';
export const SEALED_KEY = 'sealed';
/** Iterations for new envelopes. Bounded by what a phone derives in a few seconds in JavaScript. */
export const SEAL_ITERATIONS = 25_000;
export const MIN_PASSWORD_LENGTH = 8;
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
export function parseSealed(value) {
    if (!isRecord(value) || value.v !== 1 || value.kdf !== 'pbkdf2-sha256')
        return undefined;
    const { iterations, salt, iv, data, hint } = value;
    if (typeof iterations !== 'number' || !Number.isInteger(iterations) || iterations < 1_000) {
        return undefined;
    }
    if (typeof salt !== 'string' || typeof iv !== 'string' || typeof data !== 'string') {
        return undefined;
    }
    return {
        v: 1,
        kdf: 'pbkdf2-sha256',
        iterations: Math.min(iterations, 10_000_000),
        salt,
        iv,
        data,
        ...(typeof hint === 'string' && hint.trim() !== '' ? { hint: hint.slice(0, 120) } : {}),
    };
}
/** The envelope a stored document carries, if it is a protected map. */
export function sealedOf(document) {
    const root = document.nodes[document.map.rootNodeId];
    return root === undefined ? undefined : parseSealed(root.metadata?.[SEALED_KEY]);
}
export function isSealed(document) {
    return sealedOf(document) !== undefined;
}
/** What goes into the envelope: everything about the map that is content rather than a name. */
export function payloadOf(document) {
    return {
        v: 1,
        rootNodeId: document.map.rootNodeId,
        nodes: document.nodes,
        edges: document.edges,
        layoutId: document.map.layoutId,
        themeId: document.map.themeId,
        settings: document.map.settings,
    };
}
/**
 * The document a payload describes, placed in the shell's map.
 *
 * Node and edge `mapId`s are rewritten to the shell's: a map protected under one id and opened
 * under another must still be one map to the engine.
 */
export function documentFromPayload(shell, payload) {
    const mapId = shell.map.id;
    const nodes = {};
    for (const [id, node] of Object.entries(payload.nodes))
        nodes[id] = { ...node, mapId };
    const edges = {};
    for (const [id, edge] of Object.entries(payload.edges))
        edges[id] = { ...edge, mapId };
    return {
        map: {
            ...shell.map,
            rootNodeId: payload.rootNodeId,
            layoutId: payload.layoutId,
            themeId: payload.themeId,
            settings: payload.settings,
        },
        nodes: nodes,
        edges: edges,
    };
}
function parsePayload(text) {
    let value;
    try {
        value = JSON.parse(text);
    }
    catch {
        return undefined;
    }
    if (!isRecord(value) || value.v !== 1 || typeof value.rootNodeId !== 'string')
        return undefined;
    if (!isRecord(value.nodes) || !isRecord(value.edges) || !isRecord(value.settings))
        return undefined;
    if (!isRecord(value.nodes[value.rootNodeId]))
        return undefined;
    return value;
}
const encoder = new TextEncoder();
const decoder = new TextDecoder();
async function encryptPayload(payload, session, cipher) {
    const iv = cipher.randomBytes(12);
    const data = await cipher.encrypt(session.key, encoder.encode(JSON.stringify(payload)), iv);
    return {
        v: 1,
        kdf: 'pbkdf2-sha256',
        iterations: session.iterations,
        salt: session.salt,
        iv: toBase64Url(iv),
        data: toBase64Url(data),
        ...(session.hint === undefined ? {} : { hint: session.hint }),
    };
}
/** Protects a document: a new key from the password, and the envelope it opens. */
export async function sealDocument(document, password, cipher, options = {}) {
    const saltBytes = cipher.randomBytes(16);
    const iterations = options.iterations ?? SEAL_ITERATIONS;
    const hint = options.hint?.trim();
    const session = {
        key: await cipher.deriveKey(password, saltBytes, iterations),
        salt: toBase64Url(saltBytes),
        iterations,
        ...(hint === undefined || hint === '' ? {} : { hint: hint.slice(0, 120) }),
    };
    return { envelope: await encryptPayload(payloadOf(document), session, cipher), session };
}
/** A new envelope for an open session: same key, fresh nonce. What every save writes. */
export function resealDocument(document, session, cipher) {
    return encryptPayload(payloadOf(document), session, cipher);
}
/**
 * Opens a protected map with a password.
 *
 * A wrong password and altered data look the same to GCM — both fail the tag — and both are
 * reported as a wrong password, which is by far the likelier of the two. A tag that passes over
 * content that is not a map is `damaged`.
 */
export async function unsealDocument(shell, password, cipher) {
    const envelope = sealedOf(shell);
    if (envelope === undefined)
        return { ok: false, reason: 'damaged' };
    const salt = fromBase64Url(envelope.salt);
    const iv = fromBase64Url(envelope.iv);
    const data = fromBase64Url(envelope.data);
    if (salt === null || iv === null || data === null)
        return { ok: false, reason: 'damaged' };
    const key = await cipher.deriveKey(password, salt, envelope.iterations);
    let plaintext;
    try {
        plaintext = await cipher.decrypt(key, data, iv);
    }
    catch {
        return { ok: false, reason: 'wrong-password' };
    }
    const payload = parsePayload(decoder.decode(plaintext));
    if (payload === undefined)
        return { ok: false, reason: 'damaged' };
    return {
        ok: true,
        document: documentFromPayload(shell, payload),
        session: {
            key,
            salt: envelope.salt,
            iterations: envelope.iterations,
            ...(envelope.hint === undefined ? {} : { hint: envelope.hint }),
        },
    };
}
/**
 * The document a protected map is stored as: the map's row and one root topic carrying the
 * envelope. The root's text is a lock and the map's name, so a device that cannot open it — an
 * older version, a thumbnail — shows what it is instead of an empty map.
 */
export function sealedShell(shell, envelope, lockedLabel) {
    const root = shell.nodes[shell.map.rootNodeId];
    if (root === undefined)
        return shell;
    const text = `🔒 ${lockedLabel}`;
    return {
        map: shell.map,
        nodes: {
            [root.id]: {
                ...root,
                title: text,
                plainText: text,
                richText: richTextFromPlain(text),
                metadata: { [SEALED_KEY]: envelope },
            },
        },
        edges: {},
    };
}
//# sourceMappingURL=sealed.js.map